/**
 * Hero with Carousel Section JavaScript
 * Initializes and configures the Swiper carousel for the hero section's sub-banner
 */

class HeroWithCarousel {
  constructor(section) {
    this.container = section;
    this.swiperContainer = this.container.querySelector('.sub-banner-swiper');
    this.swiperWrapperId = this.swiperContainer.id;
    this.autoplaySpeed = this.container.dataset.autoplaySpeed || 10000;
    this.transitionSpeed = this.container.dataset.transitionSpeed || 3000;
    this.pauseOnHover = this.container.dataset.pauseOnHover === 'true';
    this.blockCount = parseInt(this.container.dataset.blockCount || 0);
    
    if (this.swiperContainer) {
      this.initSwiper();
    }
  }

  initSwiper() {
    // Always use ribbon/marquee effect regardless of screen size
    const useRibbonEffect = true;
    
    // Configure responsive slidesPerView based on block count
    const getResponsiveSlidesPerView = (breakpoint) => {
      // For ribbon effect, we use 'auto' instead of a fixed number
      return useRibbonEffect ? 'auto' : (
        breakpoint === 'mobile' ? 1 : 
        breakpoint === 'tablet' ? (this.blockCount > 1 ? 2 : 1) : 
        (this.blockCount > 2 ? 3 : (this.blockCount > 1 ? 2 : 1))
      );
    };
    
    // Prepare slides for ribbon effect if needed
    if (useRibbonEffect && this.blockCount > 0) {
      this.prepareRibbonEffect();
    }

    // Initialize Swiper with configuration
    const swiperConfig = {
      slidesPerView: 'auto',
      spaceBetween: 30,
      loop: true,
      loopAdditionalSlides: 10,
      centeredSlides: false,
      allowTouchMove: true,
      speed: parseInt(this.transitionSpeed),
      breakpoints: {
        320: {
          slidesPerView: getResponsiveSlidesPerView('mobile'),
          spaceBetween: 40,
        },
        750: {
          slidesPerView: getResponsiveSlidesPerView('tablet'),
          spaceBetween: 40,
        },
        990: {
          slidesPerView: getResponsiveSlidesPerView('desktop'),
          spaceBetween: 120,
        }
      },
      on: {
        init: () => {
          // Set linear transition for smoother scrolling effect
          const swiperWrapper = document.querySelector(`#${this.swiperWrapperId} .swiper-wrapper`);
          if (swiperWrapper) {
            swiperWrapper.style.transitionTimingFunction = 'linear';
          }
        }
      }
    };
    
    // Configure autoplay for marquee effect
    swiperConfig.autoplay = {
      delay: 0, // No delay between transitions
      disableOnInteraction: false
    };
    
    // Set different speeds based on screen size
    const isDesktop = window.innerWidth >= 990;
    const isTablet = window.innerWidth >= 750 && window.innerWidth < 990;
    const isMobile = window.innerWidth < 750;
    
    // Adjust speed based on screen size (slower on desktop, faster on mobile)
    if (isDesktop) {
      swiperConfig.speed = 15000; // Very slow scrolling (15 seconds per transition)
    } else if (isTablet) {
      swiperConfig.speed = 12000; // Slightly faster on tablet
    } else {
      swiperConfig.speed = 10000; // Faster on mobile
    }
    
    // Initialize the swiper with our configuration
    this.swiper = new Swiper(`#${this.swiperWrapperId}`, swiperConfig);

    // Add pause on hover functionality if enabled
    if (this.pauseOnHover) {
      this.setupPauseOnHover();
    }
    
    // Add window resize handler to adjust speed based on screen size
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleResize() {
    if (this.swiper) {
      const isDesktop = window.innerWidth >= 990;
      const isTablet = window.innerWidth >= 750 && window.innerWidth < 990;
      
      if (isDesktop) {
        this.swiper.params.speed = 15000;
      } else if (isTablet) {
        this.swiper.params.speed = 12000;
      } else {
        this.swiper.params.speed = 10000;
      }
      
      this.swiper.update();
    }
  }

  prepareRibbonEffect() {
    // For ribbon effect, we need to ensure we have enough slides
    // This method prepares the DOM if needed for a smooth ribbon effect
    const slides = this.swiperContainer.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;
    
    // If we have fewer than 6 slides, duplicate them to ensure smooth scrolling
    if (slideCount > 0 && slideCount < 6) {
      const wrapper = this.swiperContainer.querySelector('.swiper-wrapper');
      
      // Clone slides and append them to create a longer ribbon
      for (let i = 0; i < Math.min(10, 12 - slideCount); i++) {
        const slideIndex = i % slideCount;
        const clone = slides[slideIndex].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(clone);
      }
    }
  }
  
  setupPauseOnHover() {
    this.swiperContainer.addEventListener('mouseenter', () => {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.stop();
      }
    });
    
    this.swiperContainer.addEventListener('mouseleave', () => {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    });
  }
}

// Initialize the section when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.hero-section[data-section-type="hero-with-carousel"]');
  
  sections.forEach(section => {
    new HeroWithCarousel(section);
  });
});

// Support for Shopify's section rendering
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section.dataset.sectionType === 'hero-with-carousel') {
      new HeroWithCarousel(section);
    }
  });
}
